import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { firmDailyNavUrl, firmIntelligenceUrl, firmScheduleOfInvestmentsUrl } from 'common/config/urls';
import { DAILY_NAV_ENABLER } from 'common/constants/features';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type { SecondaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import { SummaryDefinitionsProvider } from 'context/SummaryDefinitionsContext';
import { FundDialogForm } from 'pages/Funds/forms';
import { useDialogFormsStore } from 'store';
import { getObjectValue } from 'utilities';
import DailyNav from './DailyNav';
import FirmSummaryFundFilterMenu from './FirmSummaryFundFilterMenu';
import FirmSummaryWorkspace from './FirmSummaryWorkspace';
import { IntelligenceProvider } from './IntelligenceContext';
import {
  INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG,
  INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG,
  INTELLIGENCE_SECONDARY_NAVIGATION_LABELS,
  INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS,
} from './intelligenceNavigation.constants';
import ScheduleOfInvestments from './ScheduleOfInvestments';
import useFirmSummaryFundFilter from './useFirmSummaryFundFilter';
import { useIntelligenceExportActions } from './useIntelligenceExportActions';

type IntelligenceRouteParams = {
  firmSlugParam: string;
  secondaryTabSlugParam: string;
  tertiaryTabSlugParam: string;
};

interface IntelligenceContentProps {
  firmSlug: string;
  activeSecondaryTabSlug: string;
  activeTertiaryTabSlug: string;
  isDailyNavEnabled: boolean;
}

const IntelligenceContent = ({
  firmSlug,
  activeSecondaryTabSlug,
  activeTertiaryTabSlug,
  isDailyNavEnabled,
}: IntelligenceContentProps) => {
  const { search } = useLocation();
  const { titleAccessory, ...menuProps } = useFirmSummaryFundFilter();
  const { secondaryNavigationActions, exportDialogs, moreActionsMenu } = useIntelligenceExportActions();
  const displayFundDialogForm = useDialogFormsStore(state => state.displayFundDialogForm);
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const handleCloseFundDialog = useCallback(() => setDisplayFundDialogForm(false), [setDisplayFundDialogForm]);

  const secondaryNavigationItems = useMemo<SecondaryNavigationLinkItem[]>(
    // Carry sticky filters (?fund=, ?date=) across tab changes so the user doesn't lose context.
    () => [
      {
        label: INTELLIGENCE_SECONDARY_NAVIGATION_LABELS.SUMMARIES,
        value: INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES,
        to: `${firmIntelligenceUrl(firmSlug)}/${INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES}/${INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG}${search}`,
      },
      {
        label: INTELLIGENCE_SECONDARY_NAVIGATION_LABELS.SCHEDULE_OF_INVESTMENTS,
        value: INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SCHEDULE_OF_INVESTMENTS,
        to: `${firmScheduleOfInvestmentsUrl(firmSlug)}${search}`,
      },
      ...(isDailyNavEnabled
        ? [
          {
            label: INTELLIGENCE_SECONDARY_NAVIGATION_LABELS.DAILY_NAV,
            value: INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.DAILY_NAV,
            to: `${firmDailyNavUrl(firmSlug)}${search}`,
          },
        ]
        : []),
    ],
    [firmSlug, isDailyNavEnabled, search]
  );

  const secondaryNavigation = useMemo(
    () => ({
      title: 'Intelligence',
      titleAccessory,
      items: secondaryNavigationItems,
      // Pages with nested sub-routes need to override the host's URL-based active matching
      // so the Summaries tab stays highlighted under any tertiary slug.
      value: activeSecondaryTabSlug,
      actions: secondaryNavigationActions,
    }),
    [activeSecondaryTabSlug, secondaryNavigationActions, secondaryNavigationItems, titleAccessory]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  const renderActiveSecondaryTab = () => {
    switch (activeSecondaryTabSlug) {
      case INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SCHEDULE_OF_INVESTMENTS:
        return <ScheduleOfInvestments />;
      case INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.DAILY_NAV:
        return <DailyNav />;
      default:
        return <FirmSummaryWorkspace firmSlug={firmSlug} activeTertiaryTabSlug={activeTertiaryTabSlug} />;
    }
  };

  return (
    <>
      {renderActiveSecondaryTab()}
      <FirmSummaryFundFilterMenu {...menuProps} />
      <FundDialogForm open={displayFundDialogForm} handleOnClose={handleCloseFundDialog} onSuccess={handleCloseFundDialog} />
      {exportDialogs}
      {moreActionsMenu}
    </>
  );
};

const SECONDARY_SLUGS = Object.values(INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS) as string[];

const Intelligence = () => {
  const { firmSlugParam, secondaryTabSlugParam, tertiaryTabSlugParam } = useParams<IntelligenceRouteParams>();
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id as number | undefined;
  const [, checkFeature] = useCheckFeatures();
  const isDailyNavEnabled = checkFeature(DAILY_NAV_ENABLER) && Boolean(firmInfo?.daily_nav_enabled);

  const navigate = useNavigate();
  const location = useLocation();

  // Legacy URLs (e.g. /intelligence/firm-summary, /intelligence/view-3) live one level
  // shallower than the current shape; promote them to /intelligence/summaries/<old slug>.
  const isLegacyShape =
    Boolean(secondaryTabSlugParam) && !SECONDARY_SLUGS.includes(secondaryTabSlugParam as string);

  const rawSecondaryTabSlug = secondaryTabSlugParam ?? INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG;
  // Guard direct navigation to the Daily NAV tab while it's feature-flagged off for this user/firm.
  // firmInfo hydrates in two phases (slim firm-list entry, then full firm detail) — don't judge
  // until daily_nav_enabled is actually present, or enabled users get bounced during the gap.
  const isDailyNavStateKnown = Boolean(firmInfo) && 'daily_nav_enabled' in getObjectValue(firmInfo);
  const isDisabledDailyNavAttempt =
    isDailyNavStateKnown &&
    rawSecondaryTabSlug === INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.DAILY_NAV &&
    !isDailyNavEnabled;

  useEffect(() => {
    if (!isLegacyShape || !firmSlugParam) {
      return;
    }
    navigate(
      {
        pathname: `${firmIntelligenceUrl(firmSlugParam)}/${INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG}/${secondaryTabSlugParam}`,
        search: location.search,
      },
      { replace: true }
    );
  }, [firmSlugParam, isLegacyShape, location.search, navigate, secondaryTabSlugParam]);

  useEffect(() => {
    if (!isDisabledDailyNavAttempt || !firmSlugParam) {
      return;
    }
    navigate(
      {
        pathname: `${firmIntelligenceUrl(firmSlugParam)}/${INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG}/${INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG}`,
        search: location.search,
      },
      { replace: true }
    );
  }, [firmSlugParam, isDisabledDailyNavAttempt, location.search, navigate]);

  if (!firmSlugParam || !firmId || isLegacyShape || isDisabledDailyNavAttempt) {
    return null;
  }

  const activeSecondaryTabSlug = rawSecondaryTabSlug;
  const activeTertiaryTabSlug = tertiaryTabSlugParam ?? INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG;

  return (
    <SummaryDefinitionsProvider entityId={firmId} summaryType="firm" fetchEnabled>
      <IntelligenceProvider>
        <IntelligenceContent
          firmSlug={firmSlugParam}
          activeSecondaryTabSlug={activeSecondaryTabSlug}
          activeTertiaryTabSlug={activeTertiaryTabSlug}
          isDailyNavEnabled={isDailyNavEnabled}
        />
      </IntelligenceProvider>
    </SummaryDefinitionsProvider>
  );
};

export default Intelligence;
